'use client';

import React from 'react';
import Sidebar from './Sidebar';
import ChannelSidebar from './ChannelSidebar';
import Header from './Header';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useTheme } from 'next-themes';
import { useDashboard } from '../DashboardContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { currentGuildId, isSidebarOpen, handleSidebarOpenChange } = useDashboard();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { theme } = useTheme();

  // 사이드바 너비에 대한 tailwind 클래스
  const serverSidebarWidth = "w-[72px]";
  const channelSidebarWidth = "w-[240px]";

  // 상황에 따른 모바일 사이드바 전체 너비 계산
  const mobileSheetWidth = currentGuildId ? "w-[328px]" : "w-[72px]";

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* 데스크탑: 서버 아이콘 사이드바 고정 표시 */}
      <div className="flex flex-1 overflow-hidden">
        {isDesktop && (
          <div className={`${serverSidebarWidth} shrink-0 h-full border-r border-border`}>
            <Sidebar />
          </div>
        )}

        {/* 모바일: 상황에 따라 너비가 달라지는 사이드바 */}
        {!isDesktop && (
          <Sheet open={isSidebarOpen} onOpenChange={handleSidebarOpenChange}>
            <SheetContent
              side="left"
              className={`p-0 flex ${mobileSheetWidth} bg-background text-foreground dark:bg-background dark:text-foreground`}
              showCloseButton={false}
            >
              <div className={`${serverSidebarWidth} shrink-0 h-full border-r border-border`}>
                <Sidebar isInDrawer={true} />
              </div>

              {/* 채널 사이드바 (서버가 선택된 경우만 표시) */}
              {currentGuildId && (
                <div className={`${channelSidebarWidth} shrink-0 h-full`}>
                  <ChannelSidebar />
                </div>
              )}
            </SheetContent>
          </Sheet>
        )}

        {/* 데스크탑: 채널 사이드바 고정 표시 (서버가 선택된 경우만) */}
        {isDesktop && currentGuildId && (
          <div className={`${channelSidebarWidth} shrink-0 h-full border-r border-border`}>
            <ChannelSidebar />
          </div>
        )}

        {/* 메인 콘텐츠 영역 */}
        <div className="flex flex-col flex-1 h-full overflow-hidden bg-page-background">
          <Header />
          <div className="flex-1 p-4 overflow-auto bg-page-background justify-items-center">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
